// @section 1 Demo Library — Atlas /library (BD "see the library" view).
//
// Surfaces every live demo so the BD team can see what exists and grab a link.
// Demos are hosted externally (demo.optiq-os.com et al); this is a link-out
// directory (opens in a new tab), not an embed. Demo building stays Adam-
// controlled — this page is read-only visibility into the current arsenal.

// @subsection 1.1 Live demo catalog (kept in sync with the *.optiq-os.com SWAs).
type Demo = { name: string; url: string; blurb: string };

const COMPONENT_LIBRARY: Demo = {
  name: 'OptiqOS Demo Library',
  url: 'https://demo.optiq-os.com',
  blurb: 'The full module library: ~70 vertical-agnostic screens (My Day, Strategy, Financials, Sales, Marketing, Operations, HR). The fastest way to show breadth.',
};

const CAPABILITY_DEMOS: Demo[] = [
  { name: 'CPA Firm Demo', url: 'https://demo4.optiq-os.com', blurb: 'End-to-end narrative for a CPA firm: My Day, Practice Portfolio, Strategy, Financials. The canonical accounting sales arc.' },
  { name: 'OptiqOS Teardown', url: 'https://audit.optiq-os.com', blurb: 'The free teardown / audit lead motion: hand a prospect a sharp read and let the software sell the next step.' },
  { name: 'Building Intelligence', url: 'https://intel.optiq-os.com', blurb: 'Permit and contractor market-intelligence demo for contractors / GCs.' },
];

const NAMED_DEMOS: Demo[] = [
  { name: 'Vertis Holdings · The CPA Rollup', url: 'https://demo5.optiq-os.com', blurb: 'The CPA-rollup story: running a multi-firm acquisition / operating play.' },
  { name: 'Helm · Practice OS', url: 'https://helmos.optiq-os.com', blurb: 'Healthcare-practice build (Helm venture). A second vertical proof point. Tied to an active relationship — use judgment.' },
  { name: 'Top Center Lacrosse', url: 'https://topcenter.optiq-os.com', blurb: 'A sports / facility business build. Shows the model is not vertical-locked.' },
  { name: 'Territory Intelligence (BCTS)', url: 'https://bcts.optiq-os.com', blurb: 'Market-sizing / territory-intelligence portal. Tied to an active relationship — use judgment.' },
];

// @section 2 Component
export default function Library() {
  return (
    <div className="v5-page" style={{ padding: 24, maxWidth: 1000 }}>
      <div className="page-head" style={{ padding: 0 }}>
        <div className="crumb"><span>Atlas</span><span className="sep">›</span><b>Demo Library</b></div>
        <div className="page-title">
          <h1>Demo Library</h1>
          <div className="sub">Every live demo. Click to open in a new tab.</div>
        </div>
      </div>

      {/* @block 2.1 Component library (hero) */}
      <div style={{ marginTop: 18 }}>
        <DemoCard demo={COMPONENT_LIBRARY} hero />
      </div>

      {/* @block 2.2 Capability / vertical demos */}
      <SectionLabel>Capability &amp; vertical demos</SectionLabel>
      <div style={{ display: 'grid', gap: 10 }}>
        {CAPABILITY_DEMOS.map((d) => <DemoCard key={d.url} demo={d} />)}
      </div>

      {/* @block 2.3 Named-example builds */}
      <SectionLabel>Named-example builds (real verticals)</SectionLabel>
      <div style={{ display: 'grid', gap: 10 }}>
        {NAMED_DEMOS.map((d) => <DemoCard key={d.url} demo={d} />)}
      </div>
    </div>
  );
}

// @section 3 UI helpers
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--brand-3)', margin: '22px 0 8px' }}>
      {children}
    </div>
  );
}

function DemoCard({ demo, hero }: { demo: Demo; hero?: boolean }) {
  return (
    <a
      href={demo.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block', textDecoration: 'none',
        border: hero ? '1px solid var(--brand-3)' : '1px solid var(--line)',
        borderRadius: 10, padding: hero ? 18 : 14,
        background: hero ? 'rgba(20,184,166,0.06)' : 'var(--bg-1, rgba(255,255,255,0.02))',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontWeight: 600, fontSize: hero ? 16 : 14, color: 'var(--info)' }}>{demo.name}</span>
        <span style={{ fontSize: 11, color: 'var(--tx-4)', whiteSpace: 'nowrap' }}>{demo.url.replace('https://', '')} ↗</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--tx-2)', marginTop: 4, lineHeight: 1.45 }}>{demo.blurb}</div>
    </a>
  );
}
